import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Button, ButtonLink, LoadingTable } from '../../components/base';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import DxTable, { DxLookup, DxTableColumn } from '../../components/common/DxTable';
import { BlockCard, PageBody, PageContainer, PageHeader } from '../../components/container';
import { BaseShowDetail } from '../../context/base/BaseShowDetail';
import { eBaseActionStatus } from '../../context/base/eBaseActionStatus';
import { ArticleCategoryModel } from '../../context/models/articles-category/ArticleCategoryModel';
import { ArticleCategoryActionTypes } from '../../context/stores/articles-category/IArticleCategoryActionsTypes';
import { actions } from '../../context/stores/rootActions';
import { AppState } from '../../context/stores/rootReducers';
import { createUUID } from '../../utils/createUUID';
import ArticleCategoryDetail from './ArticleCategoryDetail';

type IArticleCategoryPageProps = {
	titlePage: string;
	status: eBaseActionStatus;
	allArticleCategorys: ArticleCategoryModel[];
	selectedIds: number[];
	showDetail: BaseShowDetail<ArticleCategoryModel>;
	showConfirm: boolean;
	handleReloadAllData: () => void;
	handleSaveData: (data: ArticleCategoryModel) => void;
	handleDeleteData: (data: number[]) => void;
	onChangeSelectedIds: (ids: number[]) => void;
	onShowDetail: (data: BaseShowDetail<ArticleCategoryModel>) => void;
	onShowConfirm: (isShow: boolean) => void;
};

const mapStateToProps = (state: AppState) => {
	return {
		titlePage: `Chuyên mục tin bài`,
		status: state.articleCategory.status,
		allArticleCategorys: state.articleCategory.allArticleCategorys,
		selectedIds: state.articleCategory.selectedIds,
		showDetail: state.articleCategory.showDetail,
		showConfirm: state.articleCategory.showConfirm,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<ArticleCategoryActionTypes>) => ({
	handleReloadAllData: () => {
		dispatch(actions.articleCategory.getAllRequest());
	},
	handleSaveData: (data: ArticleCategoryModel) => {
		dispatch(actions.articleCategory.saveRequest(data));
	},
	handleDeleteData: (ids: number[]) => {
		dispatch(actions.articleCategory.deleteRequest(ids));
	},
	onChangeSelectedIds: (ids: number[]) => {
		dispatch(actions.articleCategory.changeSelectedIds(ids));
	},
	onShowDetail: (data: BaseShowDetail<ArticleCategoryModel>) => {
		dispatch(actions.articleCategory.showDetail(data));
	},
	onShowConfirm: (isShow: boolean) => {
		dispatch(actions.articleCategory.showConfirm(isShow));
	},
});

const ArticleCategoryPage = (props: IArticleCategoryPageProps) => {
	const {
		titlePage,
		status,
		selectedIds,
		showConfirm,
		showDetail,
		allArticleCategorys,
		handleReloadAllData,
		handleDeleteData,
		onChangeSelectedIds,
		handleSaveData,
		onShowDetail,
		onShowConfirm,
	} = props;

	useEffect(() => {
		handleReloadAllData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (status === eBaseActionStatus.reload) handleReloadAllData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const columns = useMemo((): any => {
		let result: any = [
			<DxTableColumn key={createUUID()} dataField='name' caption={`Tên`} width={150} fixed></DxTableColumn>,
			<DxTableColumn key={createUUID()} dataField='slug' minWidth={150} caption={`Slug`} fixed></DxTableColumn>,
			<DxTableColumn key={createUUID()} dataField='parentId' width={150} caption={`Chuyên mục cha`}>
				<DxLookup dataSource={allArticleCategorys} valueExpr={'id'} displayExpr={'name'} />
			</DxTableColumn>,
			<DxTableColumn key={createUUID()} dataField='keyWords' width={150} caption={`Từ khóa`}></DxTableColumn>,
			<DxTableColumn key={createUUID()} dataField='description' width={150} caption={`Mô tả`}></DxTableColumn>,
			<DxTableColumn key={createUUID()} dataField='inActive' width={80} caption={`Trạng thái`} falseText='Áp dụng' trueText='Không'></DxTableColumn>,
			<DxTableColumn key={createUUID()} dataField='isHidden' width={80} caption={`Ẩn Menu chính`} falseText='Hiển thị' trueText='Ẩn'></DxTableColumn>,
			<DxTableColumn key={createUUID()} dataField='isHiddenSub' width={80} caption={`Ẩn Menu phụ`} falseText='Hiển thị' trueText='Ẩn'></DxTableColumn>,
			<DxTableColumn key={createUUID()} dataField='isHiddenFooter' width={80} caption={`Ẩn Menu chân trang`} falseText='Hiển thị' trueText='Ẩn'></DxTableColumn>,
		];
		result.push(
			<DxTableColumn
				visibleIndex={1}
				key={createUUID()}
				width={50}
				caption={'Sửa'}
				alignment='center'
				allowExporting={true}
				fixed
				cellRender={(cell: any) => {
					return (
						<ButtonLink onClick={() => onShowDetail({ isShow: true, detailData: cell.data })} title='Sửa' icon='ni ni-edit' theme='link' />
					);
				}}
			/>
		);
		return result;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allArticleCategorys]);

	return (
		<>
			<PageContainer>
				<PageHeader title={`Danh sách ${titlePage}`}>
					<div className='nk-fmg-actions'>
						<ul className='nk-block-tools g-3'>
							{selectedIds.length > 0 && (
								<li>
									<Button
										text={`Xóa (${selectedIds.length})`}
										icon='icon ni ni-trash'
										className='d-md-inline-flex'
										theme='danger'
										onClick={() => onShowConfirm(true)}
									/>
								</li>
							)}

							<li>
								<Button
									text='Tạo mới'
									icon='icon ni ni-plus'
									theme='primary'
									className='d-md-inline-flex'
									onClick={() => onShowDetail({ isShow: true })}
								/>
							</li>
						</ul>
					</div>
				</PageHeader>
				<PageBody>
					<BlockCard>
						{status === eBaseActionStatus.loading && <LoadingTable />}
						{status !== eBaseActionStatus.loading && (
							<DxTable
								dataSource={allArticleCategorys}
								keyExpr='id'
								columns={columns}
								hasFixed={true}
								isLoading={status !== eBaseActionStatus.complete}
								defaultPageSize={50}
								filters={{
									refreshDataGrid: handleReloadAllData,
								}}
								selection={{
									mode: 'multiple',
									onSelectionChanged: (e: any) => {
										onChangeSelectedIds(e.selectedRowKeys || []);
									},
									selectedRowKeys: selectedIds,
								}}
							/>
						)}
					</BlockCard>
				</PageBody>
			</PageContainer>
			{showDetail && showDetail.isShow && (
				<>
					<ArticleCategoryDetail
						isSaving={status === eBaseActionStatus.saving}
						isShow={showDetail.isShow}
						detailModel={showDetail.detailData}
						onClose={() => onShowDetail({ isShow: false })}
						onSubmit={(data) => handleSaveData(data)}
						title={`${showDetail.detailData ? `Cập nhật` : `Thêm mới`} ${titlePage}`}
					/>
				</>
			)}
			{showConfirm && (
				<>
					<ConfirmModal
						show={showConfirm}
						innerText={`Bạn chắc chắn muốn xóa ${selectedIds.length} ${titlePage.toLowerCase()} đã chọn ?`}
						type='danger'
						onClose={() => onShowConfirm(false)}
						onConfirmed={() => handleDeleteData(selectedIds)}
						isLoading={status === eBaseActionStatus.loading || status === eBaseActionStatus.saving}
						btnConfirmText='Xóa'
						btnCloseText='Không xóa'
					/>
				</>
			)}
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(ArticleCategoryPage);
